'use client'

import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'

interface ThreeColumnProceduresImagesData {
	anchor_title?: string
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title?: string
					text?: string
					image?: {
						id: string
						filename_disk: string
						filename_download: string
						description?: string
					}
				}
			}[]
		}
	}[]
}

export const fields = [
	'anchor_title',
	'heading.title',
	'text.text',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'content_collection.site_content_collections_id.collection.item:site_content.image.id',
	'content_collection.site_content_collections_id.collection.item:site_content.image.filename_disk',
	'content_collection.site_content_collections_id.collection.item:site_content.image.filename_download',
	'content_collection.site_content_collections_id.collection.item:site_content.image.description',
] as const

interface ThreeColumnProceduresImagesProps {
	data?: ThreeColumnProceduresImagesData
}

export default function ThreeColumnProceduresImages({
	data,
}: ThreeColumnProceduresImagesProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionIdBase = data?.anchor_title || 'ThreeColumnProceduresImages'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	return (
		<section id={sectionId} data-name={data?.anchor_title || 'ThreeColumnProceduresImages'} ref={ref} className='ThreeColumnProceduresImages py-16 overflow-hidden'>
			<div className='container'>
				{/* Header */}
				<div className='flex flex-col mb-6'>
					{data?.heading?.title && (
						<FadeIn isVisible={isVisible} delay={0} direction='up'>
							<h2>{data.heading.title}</h2>
						</FadeIn>
					)}

					{data?.text?.text && (
						<FadeIn isVisible={isVisible} delay={100} direction='up'>
							<div 
							className='[&>p]:text-lg'
							dangerouslySetInnerHTML={sanitizeHTML(data.text.text)} />
						</FadeIn>
					)}
				</div>

				{/* Three Columns */}
				<div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
					{data?.content_collection?.map((item, index) => {
						const image = item.site_content_collections_id.collection.find(
							c => c.item.image
						)?.item?.image
						const titleItem =
							item.site_content_collections_id.collection.find(
								c => c.item.title
							)?.item
						const textItem = item.site_content_collections_id.collection.find(
							c => c.item.text
						)?.item

						return (
							<FadeIn
								key={item.id}
								isVisible={isVisible}
								delay={200 + index * 100}
								direction='up'
								className='flex flex-col gap-6'
							>
								{/* Image */}
								{image && (
									<div className='relative w-full h-[291px] overflow-hidden'>
										<Image
											src={getReadableImageUrl(
												image.id,
												image.filename_disk || ''
											)}
											alt={image.description || titleItem?.title || ''}
											fill
											className='object-cover'
											sizes='(max-width: 768px) 100vw, 33vw'
										/>
									</div>
								)}

								{/* Content */}
								<div className='flex flex-col gap-4'>
									{/* Title */}
									{titleItem?.title && (
										<h3 className='text-3xl uppercase tracking-wide font-bold'>
											{titleItem.title}
										</h3>
									)}

									{/* Text List */}
									{textItem?.text && (
										<div
											dangerouslySetInnerHTML={sanitizeHTML(textItem.text)}
										/>
									)}
								</div>
							</FadeIn>
						)
					})}
				</div>
			</div>
		</section>
	)
}

